import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { api, ApiError, type TournamentHubSummary } from "../lib/api";
import { formatDate } from "../lib/dateFormat";
import { SPORT_LABELS, sportLabel } from "../lib/labels";

function formatDateRange(tournament: TournamentHubSummary): string | null {
  if (!tournament.start_date && !tournament.end_date) return null;
  return `${formatDate(tournament.start_date, "Sin fecha")} → ${formatDate(tournament.end_date, "Sin fecha")}`;
}

export default function MultisportHubPage() {
  const [tournaments, setTournaments] = useState<TournamentHubSummary[]>([]);
  const [sportFilter, setSportFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.publicTournaments.list();
      setTournaments(response.data.tournaments);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudieron cargar los torneos.");
      } else {
        setError("No se pudieron cargar los torneos.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(
    () =>
      tournaments.filter(
        (t) => sportFilter === "" || (t.sport ?? "tenis") === sportFilter,
      ),
    [tournaments, sportFilter],
  );

  return (
    <section className="admin-tournaments admin-tournaments--twentyone">
      <div className="tournaments-section-head">
        <div>
          <h2>Torneos123 Multisport</h2>
          <p>Todos los torneos públicos de Torneos123 — tenis, pádel y pickleball, en un solo lugar.</p>
        </div>
      </div>

      {error && (
        <p role="alert" className="form-error">
          {error}
        </p>
      )}

      <div className="tournament-command-panel" role="search" aria-label="Filtrar torneos">
        <select
          className="tournament-command-panel__sport-filter"
          value={sportFilter}
          onChange={(event) => setSportFilter(event.target.value)}
          aria-label="Filtrar por deporte"
        >
          <option value="">Todos los deportes</option>
          {Object.entries(SPORT_LABELS).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="tournaments-empty card">
          <p className="muted">Cargando…</p>
        </div>
      ) : tournaments.length === 0 ? (
        <div className="tournaments-empty card">
          <h2>Aún no hay torneos públicos.</h2>
          <p className="muted">Vuelve pronto — se publicarán torneos de todos los deportes aquí.</p>
        </div>
      ) : visible.length === 0 ? (
        <div className="tournaments-empty card">
          <h2>No encontramos torneos.</h2>
          <p className="muted">Prueba con otro deporte o quita el filtro.</p>
          <button type="button" className="btn btn--ghost" onClick={() => setSportFilter("")}>
            Limpiar filtro
          </button>
        </div>
      ) : (
        <div className="tournament-card-grid">
          {visible.map((t) => {
            const dateRange = formatDateRange(t);
            return (
              <article key={t.id} className="tournament-card">
                <div className="tournament-card__header">
                  <div>
                    <p className="tournament-card__type">{sportLabel(t.sport)}</p>
                    <h3>{t.name}</h3>
                  </div>
                  <span className="tournament-card__badges">
                    <span className="tournament-sport-pill">{sportLabel(t.sport)}</span>
                  </span>
                </div>
                <div className="tournament-card__meta">
                  {t.venue_name && <span>{t.venue_name}</span>}
                  {dateRange && <span>{dateRange}</span>}
                </div>
                <div className="tournament-card__bottom">
                  <div className="tournament-card__actions">
                    <Link
                      className="btn btn--ghost tournament-card__open"
                      to={`/inscripcion/${t.slug}`}
                    >
                      Ver e inscribirme
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
